import type { ExploreSearch } from "./explore-search";
import {
  ALL_ACCOUNTS,
  exploreSearchToCompetitorFilters,
  exploreSearchToMyBrandFilters,
  pickPlatforms,
} from "./explore-search";
import {
  type CompetitorFilters,
  type MyBrandFilters,
} from "../types/dashboard.types";

export interface ResolveExploreFiltersContext {
  industries: string[];
  myIndustryBrandNames: string[];
  competitorBrandNames: string[];
  selectableAdAccountIds: string[];
  allowedMyBrandPlatformIds: string[];
  allowedCompetitorPlatformIds: string[];
  selectableProductCodes: string[];
  accountFallback: string[] | null;
  productFallback: {
    industryProducts: string[];
    products: string[] | null;
  };
  /** True after the competitor-industry options query succeeds, including an empty list. */
  competitorBrandsLoaded?: boolean;
  persistCustomWindow?: boolean;
}

export interface ResolvedExploreFilters {
  search: ExploreSearch;
  competitorFilters: CompetitorFilters;
  myBrandFilters: MyBrandFilters;
  /** True when URL values were pruned and the caller should replace the URL. */
  searchCorrected: boolean;
}

function pickIndustry(value: string | undefined, industries: string[]): string | undefined {
  if (!value) return undefined;
  // Authorizing list not loaded yet — keep the value so deep links survive.
  if (industries.length === 0) return value;
  return industries.includes(value) ? value : undefined;
}

function pickBrand(
  value: string | undefined,
  brandNames: string[],
  listLoaded = false,
): string | undefined {
  if (!value) return undefined;
  // Authorizing list not loaded yet — keep the value so deep links survive.
  if (brandNames.length === 0) {
    return listLoaded ? undefined : value;
  }
  return brandNames.includes(value) ? value : undefined;
}

function pickBrands(
  csv: string | undefined,
  brandNames: string[],
  listLoaded = false,
): string | undefined {
  if (!csv) return undefined;
  // Authorizing list not loaded yet — keep the raw csv so deep links survive.
  if (brandNames.length === 0) {
    return listLoaded ? undefined : csv;
  }
  const allowed = new Set(brandNames);
  const picked = csv
    .split(",")
    .map((s) => s.trim())
    .filter((name) => name && allowed.has(name));
  if (picked.length === 0) return undefined;
  return picked.join(",");
}

function pickAccounts(csv: string | undefined, accountIds: string[]): string | undefined {
  if (!csv) return undefined;
  // "All" sentinel is expanded to real ids downstream — keep it in the URL.
  if (csv === ALL_ACCOUNTS) return ALL_ACCOUNTS;
  // Authorizing list not loaded yet — keep the raw csv so deep links survive.
  if (accountIds.length === 0) return csv;
  const allowed = new Set(accountIds);
  const picked = csv
    .split(",")
    .map((s) => s.trim())
    .filter((id) => id && allowed.has(id));
  if (picked.length === 0) return undefined;
  return picked.join(",");
}

export function resolveExploreFilters(
  rawSearch: ExploreSearch,
  ctx: ResolveExploreFiltersContext & { mode: ExploreSearch["mode"] },
): ResolvedExploreFilters {
  const corrected: ExploreSearch = { ...rawSearch, mode: ctx.mode };
  const platformAllowList =
    ctx.mode === "competitors"
      ? ctx.allowedCompetitorPlatformIds
      : ctx.allowedMyBrandPlatformIds;
  const platforms = pickPlatforms(rawSearch.platforms, platformAllowList, {
    preserveAllSentinel: ctx.mode === "competitors",
  });
  corrected.platforms = platforms;

  if (ctx.mode === "competitors") {
    const myIndustry = pickIndustry(rawSearch.myIndustry, ctx.industries);
    const competitorsIndustry = pickIndustry(rawSearch.competitorsIndustry, ctx.industries);
    const myBrand = pickBrand(rawSearch.myBrand, ctx.myIndustryBrandNames);
    const mainCompetitor = pickBrand(
      rawSearch.mainCompetitor,
      ctx.competitorBrandNames,
      ctx.competitorBrandsLoaded,
    );
    const benchmark = pickBrands(
      rawSearch.benchmark,
      ctx.competitorBrandNames,
      ctx.competitorBrandsLoaded,
    );

    corrected.myIndustry = myIndustry;
    corrected.competitorsIndustry = competitorsIndustry;
    corrected.myBrand = myBrand;
    corrected.mainCompetitor = mainCompetitor;
    corrected.benchmark = benchmark;
    corrected.accounts = undefined;
  } else {
    corrected.accounts = pickAccounts(rawSearch.accounts, ctx.selectableAdAccountIds);
    corrected.myIndustry = undefined;
    corrected.myBrand = undefined;
    corrected.competitorsIndustry = undefined;
    corrected.mainCompetitor = undefined;
    corrected.benchmark = undefined;
  }

  const searchCorrected =
    corrected.platforms !== rawSearch.platforms ||
    corrected.myIndustry !== rawSearch.myIndustry ||
    corrected.myBrand !== rawSearch.myBrand ||
    corrected.competitorsIndustry !== rawSearch.competitorsIndustry ||
    corrected.mainCompetitor !== rawSearch.mainCompetitor ||
    corrected.benchmark !== rawSearch.benchmark ||
    corrected.accounts !== rawSearch.accounts;

  const persist = { persistCustomWindow: ctx.persistCustomWindow === true };
  const competitorFilters = exploreSearchToCompetitorFilters(
    corrected,
    { industryProducts: ctx.productFallback.industryProducts },
    persist,
  );
  const myBrandFilters = exploreSearchToMyBrandFilters(
    corrected,
    { products: ctx.productFallback.products },
    ctx.accountFallback,
    persist,
  );
  if (ctx.mode === "my-brand" && myBrandFilters.products !== null && ctx.selectableProductCodes.length > 0) {
    const allowed = new Set(ctx.selectableProductCodes);
    const pruned = myBrandFilters.products.filter((code) => allowed.has(code));
    myBrandFilters.products = pruned.length > 0 ? pruned : null;
  }

  return {
    search: corrected,
    competitorFilters,
    myBrandFilters,
    searchCorrected,
  };
}

export interface ResolvedExploreFiltersForStore {
  mode: ExploreSearch["mode"];
  competitorFilters: CompetitorFilters;
  myBrandFilters: MyBrandFilters;
  search: ExploreSearch;
  searchCorrected: boolean;
}

/** Resolve both mode filter sets from URL so inactive-mode selections survive mode toggles. */
export function resolveExploreFiltersForStore(
  rawSearch: ExploreSearch,
  ctx: ResolveExploreFiltersContext,
): ResolvedExploreFiltersForStore {
  const competitorResolved = resolveExploreFilters(
    { ...rawSearch, mode: "competitors" },
    { ...ctx, mode: "competitors" },
  );
  const myBrandResolved = resolveExploreFilters(
    { ...rawSearch, mode: "my-brand" },
    { ...ctx, mode: "my-brand" },
  );

  const activeResolved =
    rawSearch.mode === "my-brand" ? myBrandResolved : competitorResolved;

  return {
    mode: rawSearch.mode,
    competitorFilters: competitorResolved.competitorFilters,
    myBrandFilters: myBrandResolved.myBrandFilters,
    search: activeResolved.search,
    searchCorrected:
      competitorResolved.searchCorrected || myBrandResolved.searchCorrected,
  };
}
